import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Building2, 
  Clock, 
  CheckCircle, 
  MessageSquare, 
  Archive,
  Trash2,
  RefreshCw,
  LogOut,
  ChevronRight,
  User,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, replied: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getAuthHeader = () => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${auth}` };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/admin/verify`, {}, {
        headers: getAuthHeader()
      });
      setIsAuthenticated(true);
      fetchContacts();
      fetchStats();
    } catch (err) {
      setError('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
    setContacts([]);
    setSelectedContact(null);
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts`, {
        headers: getAuthHeader()
      });
      setContacts(response.data);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const updateContactStatus = async (contactId, status) => {
    try {
      await axios.patch(`${API}/admin/contacts/${contactId}`, { status }, {
        headers: getAuthHeader()
      });
      fetchContacts();
      fetchStats();
      if (selectedContact?.id === contactId) {
        setSelectedContact({ ...selectedContact, status });
      }
    } catch (err) {
      console.error('Failed to update contact:', err);
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`, {
        headers: getAuthHeader()
      });
      fetchContacts();
      fetchStats();
      if (selectedContact?.id === contactId) {
        setSelectedContact(null);
      }
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500';
      case 'read': return 'bg-yellow-500';
      case 'replied': return 'bg-green-500';
      case 'archived': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-[#0d1526] border border-white/10 rounded-2xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-white mb-2">Admin Login</h1>
              <p className="text-white/50 text-sm">Parler Cloud Contact Management</p>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
                <AlertCircle size={20} className="text-red-500" />
                <span className="text-red-400 text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label htmlFor="username" className="text-white/70 text-sm mb-2 block">Username</Label>
                <Input
                  id="username"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="bg-white/5 border-white/10 rounded-xl text-white"
                  placeholder="Enter username"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-white/70 text-sm mb-2 block">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="bg-white/5 border-white/10 rounded-xl text-white"
                  placeholder="Enter password"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2a5ff6] hover:bg-[#4d7ff8] text-white font-semibold py-3 rounded-xl"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#0a0f1a] pt-20">
      {/* Header */}
      <div className="bg-[#0d1526] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Contact Submissions</h1>
            <p className="text-white/50 text-sm">Manage incoming inquiries</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => { fetchContacts(); fetchStats(); }}
              className="bg-white/5 hover:bg-white/10 text-white rounded-xl"
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              className="bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0d1526] border border-white/10 rounded-2xl p-5">
            <div className="text-3xl font-bold text-white mb-1">{stats.total}</div>
            <div className="text-white/50 text-sm">Total Submissions</div>
          </div>
          <div className="bg-[#0d1526] border border-blue-500/30 rounded-2xl p-5">
            <div className="text-3xl font-bold text-blue-400 mb-1">{stats.new}</div>
            <div className="text-white/50 text-sm">New</div>
          </div>
          <div className="bg-[#0d1526] border border-yellow-500/30 rounded-2xl p-5">
            <div className="text-3xl font-bold text-yellow-400 mb-1">{stats.read}</div>
            <div className="text-white/50 text-sm">Read</div>
          </div>
          <div className="bg-[#0d1526] border border-green-500/30 rounded-2xl p-5">
            <div className="text-3xl font-bold text-green-400 mb-1">{stats.replied}</div>
            <div className="text-white/50 text-sm">Replied</div>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contact List */}
          <div className="lg:col-span-1 bg-[#0d1526] border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h2 className="font-semibold text-white">All Submissions</h2>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {contacts.length === 0 ? (
                <div className="p-8 text-center text-white/40">
                  <MessageSquare size={40} className="mx-auto mb-3 opacity-50" />
                  <p>No submissions yet</p>
                </div>
              ) : (
                contacts.map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => {
                      setSelectedContact(contact);
                      if (contact.status === 'new') {
                        updateContactStatus(contact.id, 'read');
                      }
                    }}
                    className={`p-4 border-b border-white/5 cursor-pointer transition-colors hover:bg-white/5 ${
                      selectedContact?.id === contact.id ? 'bg-white/10' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${getStatusColor(contact.status)}`}></span>
                        <span className="font-medium text-white text-sm truncate max-w-[150px]">
                          {contact.name}
                        </span>
                      </div>
                      <ChevronRight size={16} className="text-white/30" />
                    </div>
                    <p className="text-white/60 text-sm truncate mb-1">{contact.subject}</p>
                    <p className="text-white/40 text-xs">{formatDate(contact.created_at)}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Contact Detail */}
          <div className="lg:col-span-2 bg-[#0d1526] border border-white/10 rounded-2xl overflow-hidden">
            {selectedContact ? (
              <>
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-white mb-1">{selectedContact.subject}</h2>
                      <div className="flex items-center gap-4 text-sm text-white/50">
                        <span className="flex items-center gap-1">
                          <User size={14} />
                          {selectedContact.name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail size={14} />
                          {selectedContact.email}
                        </span>
                        {selectedContact.company && (
                          <span className="flex items-center gap-1">
                            <Building2 size={14} />
                            {selectedContact.company}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedContact.status)} text-white`}>
                      {selectedContact.status}
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="bg-white/5 rounded-xl p-5 mb-6">
                    <p className="text-white/80 whitespace-pre-wrap leading-relaxed">
                      {selectedContact.message}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-white/40 mb-6">
                    <Clock size={14} />
                    Submitted: {formatDate(selectedContact.created_at)}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => updateContactStatus(selectedContact.id, 'replied')}
                      className="bg-green-500/10 hover:bg-green-500/20 text-green-400 rounded-xl"
                    >
                      <CheckCircle size={16} className="mr-2" />
                      Mark as Replied
                    </Button>
                    <Button
                      onClick={() => updateContactStatus(selectedContact.id, 'archived')}
                      className="bg-white/5 hover:bg-white/10 text-white/70 rounded-xl"
                    >
                      <Archive size={16} className="mr-2" />
                      Archive
                    </Button>
                    <a
                      href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                      className="inline-flex items-center gap-2 bg-[#2a5ff6] hover:bg-[#4d7ff8] text-white font-semibold px-4 py-2 rounded-xl transition-colors"
                    >
                      <Mail size={16} />
                      Reply via Email
                    </a>
                    <Button
                      onClick={() => deleteContact(selectedContact.id)}
                      className="bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl ml-auto"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full min-h-[400px] text-white/40">
                <div className="text-center">
                  <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Select a submission to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
